import { Controller } from "@hotwired/stimulus"

type Point = [number, number]

const BEFORE_SCARE_MS = 2400
const SCARE_MS = 2600
const AFTER_SCARE_MS = 3200

// Screen corners (top-left, top-right, bottom-right, bottom-left) in % of the stage,
// measured from the laptop art's transparent cut-out.
const SCREEN_CORNERS: Point[] = [
  [5.37, 6.94],
  [55.93, 7.54],
  [61.51, 54.11],
  [10.41, 63.93],
]
// Pushes the clip and the plane under the bezel, so nothing peeks through its rounded
// corners. In % of the stage width.
const SCREEN_BLEED = 1.2

// Loops cost bytes, so phones and Save-Data visitors get the still.
const VIDEO_MEDIA_QUERY = "(min-width: 48rem)"

// Slideshow on the laptop of shared/popups/_wallpapers. Media loads one slide ahead,
// only while the popup is open.
export default class extends Controller<HTMLDialogElement> {
  static targets = ["stage", "screen", "plane", "slide", "scare", "dot"]

  declare readonly stageTarget: HTMLElement
  declare readonly screenTarget: HTMLElement
  declare readonly planeTarget: HTMLElement
  declare readonly slideTargets: HTMLElement[]
  declare readonly scareTarget: HTMLElement
  declare readonly dotTargets: HTMLElement[]
  declare timer?: number
  declare resizeObserver?: ResizeObserver
  declare playsVideo: boolean

  disconnect() {
    this.stop()
  }

  // Bound after popup#show on the same event, so an open dialog means "this one".
  start() {
    if (!this.element.open) return

    this.stop()
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    this.playsVideo = window.matchMedia(VIDEO_MEDIA_QUERY).matches && !connection?.saveData
    this.resizeObserver = new ResizeObserver(() => this.fitScreen())
    this.resizeObserver.observe(this.stageTarget)
    this.show(0)
  }

  stop() {
    window.clearTimeout(this.timer)
    this.timer = undefined
    this.resizeObserver?.disconnect()
    this.resizeObserver = undefined
    this.element.querySelectorAll("video").forEach((video) => video.pause())
  }

  show(index: number) {
    const slide = this.slideTargets[index]
    const nextIndex = (index + 1) % this.slideTargets.length

    this.slideTargets.forEach((each, eachIndex) => {
      each.classList.toggle("promo-wallpapers-slide--on", each === slide)
      if (eachIndex === index || eachIndex === nextIndex) this.load(each)

      const video = each.querySelector("video")
      if (!video) return
      if (each === slide && this.playsVideo) {
        // Autoplay can be refused (Low Power Mode); the still stays up.
        video.play().catch(() => {})
      } else {
        video.pause()
      }
    })

    if (slide !== this.scareTarget) {
      const lit = this.slideTargets.filter((each) => each !== this.scareTarget).indexOf(slide)
      this.dotTargets.forEach((dot, dotIndex) =>
        dot.classList.toggle("promo-wallpapers-dot--on", dotIndex === lit),
      )
    }

    this.stageTarget.style.setProperty("--glow", slide.dataset.glow ?? null)
    // Restart the one-shot jolt on every loop.
    this.stageTarget.classList.remove("promo-wallpapers-stage--scared")
    if (slide === this.scareTarget) {
      void this.stageTarget.offsetWidth
      this.stageTarget.classList.add("promo-wallpapers-stage--scared")
    }

    this.timer = window.setTimeout(() => this.show(nextIndex), this.durationOf(index))
  }

  load(slide: HTMLElement) {
    const still = slide.querySelector("img")!
    if (!still.getAttribute("src")) still.src = still.dataset.src!

    const video = slide.querySelector("video")
    video?.toggleAttribute("hidden", !this.playsVideo)
    if (!video || !this.playsVideo || video.getAttribute("src")) return

    video.poster = still.src
    video.src = video.dataset.src!
  }

  durationOf(index: number) {
    const scareIndex = this.slideTargets.indexOf(this.scareTarget)
    if (index < scareIndex) return BEFORE_SCARE_MS
    if (index === scareIndex) return SCARE_MS
    return AFTER_SCARE_MS
  }

  // Maps the flat plane onto the tilted screen: a matrix3d that sends the plane's
  // corners to the screen's.
  fitScreen() {
    const width = this.stageTarget.clientWidth
    const height = this.stageTarget.clientHeight
    if (!width || !height) return

    // Close to the screen's own proportions, so object-fit: cover crops sensibly.
    const planeWidth = Math.round(width * 0.52)
    const planeHeight = Math.round(planeWidth * 0.66)
    this.planeTarget.style.width = `${planeWidth}px`
    this.planeTarget.style.height = `${planeHeight}px`

    const corners = SCREEN_CORNERS.map(([x, y]): Point => [(x / 100) * width, (y / 100) * height])
    const centerX = corners.reduce((sum, [x]) => sum + x, 0) / corners.length
    const centerY = corners.reduce((sum, [, y]) => sum + y, 0) / corners.length
    const bleed = (SCREEN_BLEED / 100) * width
    const screen = corners.map(([x, y]): Point => {
      const distance = Math.hypot(x - centerX, y - centerY) || 1
      return [x + ((x - centerX) / distance) * bleed, y + ((y - centerY) / distance) * bleed]
    })
    this.screenTarget.style.clipPath = `polygon(${screen.map(([x, y]) => `${x.toFixed(1)}px ${y.toFixed(1)}px`).join(", ")})`

    const plane: Point[] = [
      [0, 0],
      [planeWidth, 0],
      [planeWidth, planeHeight],
      [0, planeHeight],
    ]
    const h = solveHomography(plane, screen)
    if (!h) return

    // matrix3d is column-major; the projective terms go in the fourth row.
    const matrix = [h[0], h[3], 0, h[6], h[1], h[4], 0, h[7], 0, 0, 1, 0, h[2], h[5], 0, 1]
    this.planeTarget.style.transform = `matrix3d(${matrix.map((value) => value.toFixed(6)).join(",")})`
  }
}

// Solves the eight unknowns of x' = (ax + by + c) / (gx + hy + 1),
// y' = (dx + ey + f) / (gx + hy + 1) for four point pairs by Gaussian elimination.
// Null when the corners are degenerate (three in a line).
const solveHomography = (from: Point[], to: Point[]) => {
  const rows = from.flatMap(([x, y], i) => {
    const [u, v] = to[i]
    return [
      [x, y, 1, 0, 0, 0, -u * x, -u * y, u],
      [0, 0, 0, x, y, 1, -v * x, -v * y, v],
    ]
  })

  for (let col = 0; col < 8; col += 1) {
    let pivot = col
    for (let row = col + 1; row < 8; row += 1) {
      if (Math.abs(rows[row][col]) > Math.abs(rows[pivot][col])) pivot = row
    }
    if (Math.abs(rows[pivot][col]) < 1e-9) return null
    ;[rows[col], rows[pivot]] = [rows[pivot], rows[col]]

    for (let row = 0; row < 8; row += 1) {
      if (row === col) continue
      const factor = rows[row][col] / rows[col][col]
      for (let c = col; c < 9; c += 1) rows[row][c] -= factor * rows[col][c]
    }
  }

  return rows.map((row, i) => row[8] / row[i])
}
